/**
 * Portal data access for the route loaders.
 *
 * Replaces `portal.server.ts`, which forwarded the browser's cookies from a
 * Next.js server render. The requests are the same; they are simply made from
 * the browser now, and are expressed as React Query options so a loader's
 * `ensureQueryData` and the component's `useQuery` share one cache entry and
 * one in-flight request.
 *
 * The `null`-on-failure contract is preserved: a portal page renders empty
 * rather than erroring when the backend is unreachable.
 */

import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { apiFetch, apiUrl } from "@/core/lib/api";
import type { PortalAttendanceRecord } from "@/core/lib/attendance-api";
import { canUseFeature, type FeatureKey } from "@/core/lib/plans";
import type {
  PortalAnnouncement,
  PortalAsset,
  PortalAssetHistoryItem,
  PortalDetailField,
  PortalDocument,
  PortalLeave,
  PortalMe,
  PortalOnboardingTask,
  PortalPolicy,
  PortalRequest,
  PortalSignedTask,
  PortalSignTask,
} from "@/core/lib/portal-api";

/** Generic GET for the portal's routes. `null` on any failure. */
async function portalGet<T>(path: string): Promise<T | null> {
  try {
    const res = await apiFetch(apiUrl(path), {
      method: "GET",
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch (err) {
    console.error("[portal] backend unreachable", err);
    return null;
  }
}

const query = <T>(key: string, path: string, fallback: T) => ({
  queryKey: ["portal", key] as const,
  queryFn: async () => (await portalGet<T>(path)) ?? fallback,
});

/** The signed-in employee's own profile — backs the portal shell and home. */
export const portalMeQuery = {
  queryKey: ["portal", "me"] as const,
  queryFn: () => portalGet<PortalMe>("/api/portal/me"),
};

export const portalRequestsQuery = query<PortalRequest[]>("requests", "/api/portal/requests", []);
export const portalDocumentsQuery = query<PortalDocument[]>(
  "documents",
  "/api/portal/documents",
  [],
);
export const portalSigningQuery = query<PortalSignTask[]>("signing", "/api/portal/signing", []);
export const portalSignedQuery = query<PortalSignedTask[]>(
  "signed",
  "/api/portal/signing/signed",
  [],
);
export const portalDetailsQuery = query<PortalDetailField[]>("details", "/api/portal/details", []);
export const portalAssetsQuery = query<PortalAsset[]>("assets", "/api/portal/assets", []);
export const portalAnnouncementsQuery = query<PortalAnnouncement[]>(
  "announcements",
  "/api/portal/announcements",
  [],
);
export const portalPoliciesQuery = query<PortalPolicy[]>("policies", "/api/portal/policies", []);
export const portalOnboardingQuery = query<PortalOnboardingTask[]>(
  "onboarding",
  "/api/portal/onboarding",
  [],
);

export const portalAttendanceQuery = query<PortalAttendanceRecord[]>(
  "attendance",
  "/api/portal/attendance",
  [],
);

/** Current and previous asset assignments with their event and issue history. */
export const portalAssetHistoryQuery = {
  queryKey: ["portal", "asset-history"] as const,
  queryFn: async (): Promise<PortalAssetHistoryItem[]> => {
    const data = await portalGet<{ items: PortalAssetHistoryItem[] }>("/api/portal/assets/history");
    return data?.items ?? [];
  },
};

export const portalLeaveQuery = {
  queryKey: ["portal", "leave"] as const,
  queryFn: async (): Promise<PortalLeave> =>
    (await portalGet<PortalLeave>("/api/portal/leave")) ?? {
      today: new Date().toISOString().slice(0, 10),
      settings: {
        types: [],
        allowNegativeBalance: false,
        countBusinessDaysOnly: false,
        minNoticeDays: 0,
      },
      balances: [],
      history: [],
    },
};

/**
 * Loader guard for a plan-gated portal page: redirects to the portal home when
 * the employer's plan doesn't include `feature`, so a hidden section can't be
 * reached by typing its URL. Mirrors the nav-level hiding in `PortalSidebar`
 * and the API-level `assertFeature` gate on the backend.
 *
 * When `/me` can't be read (`null`), the guard stays out of the way rather than
 * bouncing the employee — the API is gated regardless, so a page that slips
 * through simply renders empty. Same fail-open-to-visible stance the portal
 * shell already takes.
 */
export async function requirePortalFeature(
  queryClient: QueryClient,
  feature: FeatureKey,
): Promise<void> {
  const me = await queryClient.ensureQueryData(portalMeQuery);
  if (me && !canUseFeature(feature, me.plan)) {
    throw redirect({ to: "/portal" });
  }
}
